/**
 * Environment configuration for the CarDoo app
 * This file centralizes all environment-related configuration.
 *
 * IMPORTANT: This file enforces the presence of Supabase credentials.
 * The application will throw an error on startup if SUPABASE_URL or
 * SUPABASE_ANON_KEY are not provided via a .env file. This is a
 * security measure to prevent using insecure fallback credentials.
 */

import Constants from 'expo-constants';
import logger from './logger';

// Try to get environment variables from Expo Constants, which are populated by app.config.js from the .env file.
const getExpoConstantsEnv = () => {
  try {
    // Check if running in a Jest test environment
    if (process.env.JEST_WORKER_ID) {
      return {
        supabase: {
          url: process.env.EXPO_PUBLIC_SUPABASE_URL,
          anonKey: process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY,
        },
        posthog: {
          apiKey: process.env.EXPO_PUBLIC_POSTHOG_API_KEY,
          host: process.env.EXPO_PUBLIC_POSTHOG_HOST,
          frontendDomain: process.env.EXPO_PUBLIC_FRONTEND_DOMAIN,
          proxyPath: process.env.EXPO_PUBLIC_POSTHOG_PROXY_PATH,
        },
        sentry: {
          dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
          environment: process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT,
          org: process.env.EXPO_PUBLIC_SENTRY_ORG,
          project: process.env.EXPO_PUBLIC_SENTRY_PROJECT,
          filterMode: process.env.EXPO_PUBLIC_SENTRY_FILTER_MODE,
        },
        gtmContainerId: process.env.EXPO_PUBLIC_GTM_CONTAINER_ID,
        isPaywallEnabled: process.env.EXPO_PUBLIC_PAYWALL_ENABLED !== 'false',
        // D-017: negotiation feature flag (off by default until Step 6 worker is wired)
        isNegotiationEnabled: process.env.EXPO_PUBLIC_NEGOTIATION_ENABLED === 'true',
      };
    }

    const expoConstants = Constants.expoConfig?.extra;

    if (expoConstants) {
      return {
        supabase: {
          url: expoConstants.supabaseUrl,
          anonKey: expoConstants.supabaseAnonKey,
        },
        posthog: {
          apiKey: expoConstants.posthogApiKey,
          host: expoConstants.posthogHost,
          frontendDomain: expoConstants.frontendDomain,
          proxyPath: expoConstants.posthogProxyPath,
        },
        sentry: {
          dsn: expoConstants.sentryDsn,
          environment: expoConstants.sentryEnvironment,
          org: expoConstants.sentryOrg,
          project: expoConstants.sentryProject,
          filterMode: expoConstants.sentryFilterMode,
        },
        cloudflareEdgeFunctionsUrl: expoConstants.cloudflareEdgeFunctionsUrl,
        turnstileSiteKey: expoConstants.turnstileSiteKey,
        cdnBaseUrl: expoConstants.cdnBaseUrl,
        gtmContainerId: expoConstants.gtmContainerId,
        isPlaceholderFlowActive: expoConstants.isPlaceholderFlowActive,
        useMockData: expoConstants.useMockData,
        isPaywallEnabled: expoConstants.isPaywallEnabled,
        // D-017: negotiation feature flag
        isNegotiationEnabled: expoConstants.isNegotiationEnabled,
      };
    }

    return null;
  } catch (error) {
    // This might happen in environments where Expo Constants are not available.
    logger.error('Error accessing Expo Constants:', error);
    return null;
  }
};

// The environment configuration, now strictly relying on the loaded constants.
const loadedEnv = getExpoConstantsEnv();

const env = {
  supabase: {
    url: loadedEnv?.supabase?.url,
    anonKey: loadedEnv?.supabase?.anonKey,
  },
  posthog: {
    apiKey: loadedEnv?.posthog?.apiKey,
    host: loadedEnv?.posthog?.host,
    frontendDomain: loadedEnv?.posthog?.frontendDomain,
    proxyPath: loadedEnv?.posthog?.proxyPath,
  },
  sentry: {
    dsn: loadedEnv?.sentry?.dsn,
    environment: loadedEnv?.sentry?.environment,
    org: loadedEnv?.sentry?.org,
    project: loadedEnv?.sentry?.project,
    filterMode: loadedEnv?.sentry?.filterMode || 'normal',
  },
  cloudflareEdgeFunctionsUrl: loadedEnv?.cloudflareEdgeFunctionsUrl,
  turnstileSiteKey: loadedEnv?.turnstileSiteKey,
  cdnBaseUrl: loadedEnv?.cdnBaseUrl,
  gtmContainerId: loadedEnv?.gtmContainerId,
  isPlaceholderFlowActive: loadedEnv?.isPlaceholderFlowActive,
  useMockData: loadedEnv?.useMockData,
  isPaywallEnabled: loadedEnv?.isPaywallEnabled ?? true,
  // D-017: negotiation is off by default until Step 6 worker is wired.
  // Fallback: false (shows "Coming soon" / routes to dealer site instead of worker call).
  isNegotiationEnabled: loadedEnv?.isNegotiationEnabled ?? false,
};

// --- Configuration Validation ---

// CRITICAL: Validate that Supabase credentials are provided.
// If they are not, throw an error to halt the application startup immediately.
if (!env.supabase.url || !env.supabase.anonKey) {
  throw new Error(
    'Supabase URL or Anon Key is missing. Please ensure you have a .env file in the project root with EXPO_PUBLIC_SUPABASE_URL and EXPO_PUBLIC_SUPABASE_ANON_KEY defined. Refer to README-SUPABASE.md for setup instructions.'
  );
}

// NON-CRITICAL: Warn if PostHog is not configured, but allow the app to run.
// This allows analytics to be optional, especially in early development.
if (!env.posthog.apiKey || env.posthog.apiKey.includes('YOUR_POSTHOG_DEV_API_KEY')) {
  logger.warn(
    'PostHog API Key is not configured or is using a fallback value. Analytics will be disabled. Check your .env file and README-POSTHOG.md if this is unexpected.'
  );
}

// NON-CRITICAL: Warn if Sentry is not configured, but allow the app to run.
// This allows error tracking to be optional, especially during initial development.
if (!env.sentry.dsn) {
  logger.warn(
    'Sentry DSN is not configured. Error tracking and performance monitoring will be disabled. This is optional but recommended for production.'
  );
}

export default env;
